import { faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Chip, Collapse, Grid, IconButton, InputAdornment } from '@mui/material';
import { CdhAccent } from 'components/general/Accent/variants';
import PriorityDecoration from 'components/general/PriorityDecoration';
import LabeledCheckbox from 'components/general/inputs/LabeledCheckbox';
import LabeledInput from 'components/general/inputs/LabeledInput';
import { ICombinationalLogic, ILogicalConfiguration, TRoutine } from 'components/general/types/cdh';
import WidgetTable from 'components/general/widgets/WidgetTable';
import formStyles from 'components/general/wizards/WizardSegment/styles';
import { ItemTypes } from 'config';
import { useActiveEntities } from 'hooks';
import { ITableBlock } from 'hooks/useSelectBlocks';
import { ChangeEvent, memo, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { XYCoord, useDrag, useDrop } from 'react-dnd';
import { IRoutineFormProps } from '.';
import { conditionsTableColumns } from '../../ConditionsSegment';
import { RoutineContext, useRoutineState } from '../RoutinesWidget';
import { addBlockToState } from '../RoutinesWidget/utils';
import useStyles from './styles';

export const LogicalConfigurationForm = memo(({ id }: IRoutineFormProps) => {
  const { classes, cx } = formStyles();
  const { data: config, setValue } = useRoutineState<ILogicalConfiguration>(id);
  const { conditions } = useActiveEntities();
  const [limitDurationAndFrequency, setLimitDurationAndFrequency] = useState(
    config.minOccurrenceDuration?.min != null ||
      config.maxOccurrenceDuration?.min != null ||
      config.minTimeBetweenOccurrences?.min != null,
  );
  const [tableData, setTableData] = useState<ITableBlock[]>(
    conditions.map((condition) => ({
      ...condition,
      tableData: { checked: config.conditions.includes(condition.id) },
    })),
  );

  return (
    <div className={classes.inputs}>
      <LabeledCheckbox
        label="Limit Duration and Frequency"
        value={limitDurationAndFrequency}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          setLimitDurationAndFrequency(e.target.checked);
          const defaultDuration = null;
          setValue({
            ...config,
            minOccurrenceDuration: defaultDuration,
            maxOccurrenceDuration: defaultDuration,
            minTimeBetweenOccurrences: defaultDuration,
          });
        }}
      />
      {limitDurationAndFrequency && (
        <div className={cx(classes.inputGroup, classes.indent)}>
          <LabeledInput
            value={config.minOccurrenceDuration?.min}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              e.target.value
                ? setValue(e.target.value, 'minOccurrenceDuration.min')
                : setValue(null, 'minOccurrenceDuration')
            }
            label="Min Occurrence Duration"
            type="number"
            endAdornment={<InputAdornment position="end">{'min'}</InputAdornment>}
            optional
          />
          <LabeledInput
            value={config.maxOccurrenceDuration?.min}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              e.target.value
                ? setValue(e.target.value, 'maxOccurrenceDuration.min')
                : setValue(null, 'maxOccurrenceDuration')
            }
            label="Max Occurrence Duration"
            type="number"
            endAdornment={<InputAdornment position="end">{'min'}</InputAdornment>}
            optional
          />
          <LabeledInput
            value={config.minTimeBetweenOccurrences?.min}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              e.target.value
                ? setValue(e.target.value, 'minTimeBetweenOccurrences.min')
                : setValue(null, 'minTimeBetweenOccurrences')
            }
            label="Min Time Between Occurrences"
            type="number"
            endAdornment={<InputAdornment position="end">{'min'}</InputAdornment>}
            optional
          />
        </div>
      )}
      <div className={classes.inputGroup}>
        <CdhAccent header="Conditions">
          <WidgetTable
            className={classes.table}
            columns={conditionsTableColumns}
            data={tableData}
            setData={setTableData}
            onSelectionChange={(data: ITableBlock[]) =>
              setValue(
                data.filter(({ tableData }) => tableData.checked).map(({ id }) => id),
                'conditions',
              )
            }
            emptyMessage={'No conditions found'}
            title="Select Conditions"
            search={true}
            selection={true}
          />
        </CdhAccent>
      </div>
    </div>
  );
});
LogicalConfigurationForm.displayName = 'LogicalConfigurationForm';

interface IDragItem {
  id: string;
  index: number;
}

const LogicalConfig = ({
  index,
  config,
  setPriority,
  isDefault,
  setDefault,
  expanded,
  toggleExpanded,
  closeAll,
}: {
  index: number;
  config: ILogicalConfiguration;
  setPriority: (dragIndex: number, hoverIndex: number) => void;
  isDefault: boolean;
  setDefault: (id: string | undefined) => void;
  expanded: boolean;
  toggleExpanded: () => void;
  closeAll: () => void;
}) => {
  const { classes } = useStyles();
  const ref = useRef<HTMLDivElement>(null);
  const { state } = useContext(RoutineContext);

  const [{ handlerId }, drop] = useDrop<IDragItem, void, { handlerId: unknown }>({
    accept: ItemTypes.LOGICAL_CONFIGURATION,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      setPriority(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.LOGICAL_CONFIGURATION,
    item: () => {
      return { id: config.id, index };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  useEffect(() => {
    if (isDragging) closeAll();
  }, [isDragging]); // eslint-disable-line react-hooks/exhaustive-deps

  drop(drag(ref));
  return (
    <div>
      <div
        className={classes.configItem}
        ref={ref}
        data-handler-id={handlerId}
        style={{ opacity: isDragging ? 0 : 1 }}
      >
        <Grid container spacing={1} alignItems={'center'}>
          <Grid item xs={4}>
            <h6 style={{ overflowWrap: 'break-word' }}>
              {(state.blocks[config.routine] as TRoutine).name}
            </h6>
          </Grid>
          <Grid item xs={4}>
            <h6>{config.conditions.length > 0 && `${config.conditions.length} Conditions`}</h6>
          </Grid>
          <Grid item xs={3}>
            <Chip
              variant={isDefault ? 'filled' : 'outlined'}
              color={isDefault ? 'primary' : 'default'}
              label="default"
              size="small"
              onClick={() => {
                setDefault(isDefault ? undefined : config.routine);
              }}
            />
          </Grid>
          <Grid item xs={1} style={{ paddingLeft: 0 }}>
            <IconButton
              style={{ width: 30, height: 30, fontSize: 16 }}
              onClick={() => {
                toggleExpanded();
              }}
            >
              <FontAwesomeIcon icon={expanded ? faChevronDown : faChevronRight} />
            </IconButton>
          </Grid>
        </Grid>
      </div>
      <Collapse in={expanded} unmountOnExit key={config.id}>
        <LogicalConfigurationForm id={config.id} />
      </Collapse>
    </div>
  );
};

const CombinationalLogicForm = ({ id }: IRoutineFormProps) => {
  const { classes } = useStyles();
  const { state, setState } = useContext(RoutineContext);
  const { data: routine, setValue } = useRoutineState<ICombinationalLogic>(id);
  const [expanded, setExpanded] = useState(new Set<string>());
  const configs = routine.logicalConfigurations
    ?.map((config) => state.blocks[config] as ILogicalConfiguration)
    .sort((a, b) => b.priority - a.priority);

  const setPriority = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      setState((prev) => {
        const dragId = configs[dragIndex]?.id;
        const hoverId = configs[hoverIndex]?.id;
        if (!dragId || !hoverId) return prev;
        const newState = {
          blocks: { ...prev.blocks },
          dirtyBlocks: [...prev.dirtyBlocks],
          deletedBlocks: [...prev.deletedBlocks],
        };
        const hoverPriority = (prev.blocks[dragId] as ILogicalConfiguration).priority;
        newState.blocks[dragId] = {
          ...newState.blocks[dragId],
          priority: (newState.blocks[hoverId] as ILogicalConfiguration).priority,
        };
        addBlockToState({ ...newState.blocks[hoverId], priority: hoverPriority }, newState);
        return newState;
      });
    },
    [setState, configs],
  );

  if (!configs?.length) return null;

  return (
    <div style={{ display: 'flex', minWidth: 400 }}>
      <PriorityDecoration className={classes.decoration} />
      <div style={{ minWidth: 350 }}>
        {configs.map((config, i) => (
          <LogicalConfig
            key={config.id}
            index={i}
            config={config}
            setPriority={setPriority}
            isDefault={routine.defaultSubroutine === config.routine}
            setDefault={(id: string | undefined) => setValue(id, 'defaultSubroutine')}
            expanded={expanded.has(config.id)}
            toggleExpanded={() =>
              setExpanded((prev) => {
                prev.has(config.id) ? prev.delete(config.id) : prev.add(config.id);
                return new Set(prev);
              })
            }
            closeAll={() => setExpanded(new Set<string>())}
          />
        ))}
      </div>
    </div>
  );
};

export default CombinationalLogicForm;
